import { DEFAULT_THEME_SETTINGS } from '../renderer/context.js';
import { buildDefaultCollectionDocument, buildDefaultFooterDocument, buildDefaultHeaderDocument, buildDefaultHomeDocument, buildDefaultProductDocument, } from './default/index.js';
/**
 * Ordered as merchandised on the Themes page. ONLY themes that actually exist: a registry entry
 * without `settings` + `documents` is a poster for a product we cannot deliver, so it does not
 * ship. The `coming-soon` status stays on the type (and `applyTheme` still guards on it) so a
 * genuinely announced theme can land later — today nothing claims it.
 */
export const THEME_REGISTRY = [
    {
        id: 'books',
        name: 'Folio',
        niche: 'LIVRES & LIBRAIRIE',
        pitch: 'Le goût d’un libraire, pas un algorithme — la table de mise en avant cliquable et l’extrait en pleine page.',
        // Vert bouteille du rayonnage, laiton, papier, encre brune : la vraie palette du thème.
        palette: ['#1E4034', '#B58A4B', '#F7F1E6', '#241F19'],
        status: 'available',
        isNew: true,
    },
    {
        id: 'homeware',
        name: 'Hearth',
        niche: 'MAISON & ART DE VIVRE',
        pitch: 'La pièce avant l’objet — acheter par pièce, la matière en clair, et la maison qu’on obtient.',
        // Olive, laiton, plâtre chaud, encre : la vraie palette du thème. L'accent n'est pas chaud, le FOND
        // l'est — trois thèmes occupent déjà le rouge-brun, un quatrième serait invisible dans la grille.
        palette: ['#4A5240', '#C8A46B', '#F2EDE4', '#241F1A'],
        status: 'available',
        isNew: true,
    },
    {
        id: 'tech',
        name: 'Onyx',
        niche: 'ÉLECTRONIQUE & AUDIO',
        pitch: 'Fond noir, produit éclairé — le comparatif des modèles avant le discours de marque.',
        // Cyan électrique, graphite élevé, fond noir, encre : la vraie palette du thème.
        palette: ['#2ED0EE', '#141922', '#0B0E13', '#EEF2F6'],
        status: 'available',
        isNew: true,
    },
    {
        id: 'supplements',
        name: 'Peak',
        niche: 'COMPLÉMENTS & NUTRITION',
        pitch: 'La confiance avant la promesse — dosages affichés, analyses de lot, protocole du premier mois.',
        // Accent lime, appui vert pâle, fond blanc froid, encre : la vraie palette du thème.
        palette: ['#86E01E', '#E6ECDF', '#F6F7F4', '#10140F'],
        status: 'available',
    },
    {
        id: 'coffee',
        name: 'Roast',
        niche: 'CAFÉ & TORRÉFACTION',
        pitch: 'Le profil de tasse avant le discours — origine, intensité, méthode, torréfaction datée.',
        palette: ['#43281A', '#C08A4E', '#F3EADC', '#2A1C13'],
        status: 'available',
    },
    {
        id: 'toys',
        name: 'Pogo',
        niche: 'JOUETS & ENFANTS',
        pitch: 'Pensé pour les parents — acheter par âge, la chambre cliquable, la sécurité en clair.',
        // Teal de marque, corail des aplats, jaune des bandes, papier. Les pastilles disent la PAGE,
        // pas les jetons du blob : depuis la refonte « livre d'enfant », le corail et le jaune couvrent
        // plus de surface que l'encre, et une pastille encre sur fond crème ne montrait rien.
        palette: ['#2C6A63', '#F0876B', '#F6C64B', '#FDF6EA'],
        status: 'available',
    },
    {
        id: 'beauty',
        name: 'Bloom',
        niche: 'BEAUTÉ & COSMÉTIQUE',
        pitch: 'La preuve avant le discours — avant/après, résultats chiffrés et actifs en clair.',
        palette: ['#B75A44', '#FBF6F1', '#E8D9CC', '#2A2320'],
        status: 'available',
    },
    {
        id: 'electronics',
        name: 'Volt',
        niche: 'ELECTRONICS',
        pitch: 'Spec-driven and confident — built for gadgets, audio and accessories.',
        palette: ['#000000', '#ffffff', '#f5f6f7', '#3f3f46'],
        status: 'available',
    },
    {
        id: 'default',
        name: 'Atlas',
        niche: 'ALL-PURPOSE',
        pitch: 'The house theme — clean, versatile, conversion-ready.',
        // Accent 1 / accent 2 first — the theme's real palette (mirrored in the dashboard catalog).
        palette: ['#2f5f8a', '#d2ebff', '#ffffff', '#101012'],
        status: 'available',
    },
];
/** Listing lookup by id (=== ThemeSettings.presetId). Unknown/absent id → undefined. Meta ONLY: sync. */
export function getThemeListing(id) {
    return THEME_REGISTRY.find((t) => t.id === id);
}
/**
 * One dynamic import per theme, so Rollup emits one CHUNK per theme and a seller downloads the one
 * they opened. Written out longhand rather than built from a template string: a bare
 * `import(`./${id}/index.js`)` compiles to a glob that pulls every theme back into the graph, which
 * is precisely the bundle this split exists to undo.
 */
const PAYLOAD_LOADERS = {
    books: async () => {
        const m = await import('./books/index.js');
        return {
            settings: m.FOLIO_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildFolioHomeDocument(locale),
                product: (locale) => m.buildFolioProductDocument(locale),
                collection: (locale) => m.buildFolioCollectionDocument(locale),
                header: (locale) => m.buildFolioHeaderDocument(locale),
                footer: (locale) => m.buildFolioFooterDocument(locale),
            },
            demoProduct: m.FOLIO_DEMO_PRODUCT,
        };
    },
    homeware: async () => {
        const m = await import('./homeware/index.js');
        return {
            settings: m.HEARTH_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildHearthHomeDocument(locale),
                product: (locale) => m.buildHearthProductDocument(locale),
                collection: (locale) => m.buildHearthCollectionDocument(locale),
                header: (locale) => m.buildHearthHeaderDocument(locale),
                footer: (locale) => m.buildHearthFooterDocument(locale),
            },
            demoProduct: m.HEARTH_DEMO_PRODUCT,
        };
    },
    tech: async () => {
        const m = await import('./tech/index.js');
        return {
            settings: m.ONYX_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildOnyxHomeDocument(locale),
                product: (locale) => m.buildOnyxProductDocument(locale),
                collection: (locale) => m.buildOnyxCollectionDocument(locale),
                header: (locale) => m.buildOnyxHeaderDocument(locale),
                footer: (locale) => m.buildOnyxFooterDocument(locale),
            },
            demoProduct: m.ONYX_DEMO_PRODUCT,
        };
    },
    supplements: async () => {
        const m = await import('./supplements/index.js');
        return {
            settings: m.PEAK_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildPeakHomeDocument(locale),
                product: (locale) => m.buildPeakProductDocument(locale),
                collection: (locale) => m.buildPeakCollectionDocument(locale),
                header: (locale) => m.buildPeakHeaderDocument(locale),
                footer: (locale) => m.buildPeakFooterDocument(locale),
            },
            demoProduct: m.PEAK_DEMO_PRODUCT,
        };
    },
    coffee: async () => {
        const m = await import('./coffee/index.js');
        return {
            settings: m.SBAH_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildSbahHomeDocument(locale),
                product: (locale) => m.buildSbahProductDocument(locale),
                collection: (locale) => m.buildSbahCollectionDocument(locale),
                header: (locale) => m.buildSbahHeaderDocument(locale),
                footer: (locale) => m.buildSbahFooterDocument(locale),
            },
            demoProduct: m.SBAH_DEMO_PRODUCT,
        };
    },
    toys: async () => {
        const m = await import('./toys/index.js');
        return {
            settings: m.POGO_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildPogoHomeDocument(locale),
                product: (locale) => m.buildPogoProductDocument(locale),
                collection: (locale) => m.buildPogoCollectionDocument(locale),
                header: (locale) => m.buildPogoHeaderDocument(locale),
                footer: (locale) => m.buildPogoFooterDocument(locale),
            },
            demoProduct: m.POGO_DEMO_PRODUCT,
        };
    },
    beauty: async () => {
        const m = await import('./beauty/index.js');
        return {
            settings: m.BLOOM_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildBloomHomeDocument(locale),
                product: (locale) => m.buildBloomProductDocument(locale),
                collection: (locale) => m.buildBloomCollectionDocument(locale),
                header: (locale) => m.buildBloomHeaderDocument(locale),
                footer: (locale) => m.buildBloomFooterDocument(locale),
            },
            demoProduct: m.BLOOM_DEMO_PRODUCT,
        };
    },
    electronics: async () => {
        const m = await import('./electronics/index.js');
        return {
            settings: m.ELECTRONICS_THEME_SETTINGS,
            documents: {
                index: (locale) => m.buildElectronicsHomeDocument(locale),
                product: (locale) => m.buildElectronicsProductDocument(locale),
                header: (locale) => m.buildElectronicsHeaderDocument(locale),
                footer: (locale) => m.buildElectronicsFooterDocument(locale),
            },
            demoProduct: m.ELECTRONICS_DEMO_PRODUCT,
        };
    },
    // ATLAS IS THE EXCEPTION, and deliberately static. Its builders are also the NEW-PAGE seed — creating
    // or duplicating a page builds an Atlas document synchronously — so `themes/default/index.js` is in the
    // entry chunk whatever this line says. Written as a dynamic import it bought nothing and cost a Rollup
    // warning ("dynamically imported … but also statically imported, dynamic import will not move module").
    default: async () => ({
        settings: DEFAULT_THEME_SETTINGS,
        documents: {
            index: (locale) => buildDefaultHomeDocument(locale),
            product: (locale) => buildDefaultProductDocument(locale),
            collection: (locale) => buildDefaultCollectionDocument(locale),
            header: (locale) => buildDefaultHeaderDocument(locale),
            footer: (locale) => buildDefaultFooterDocument(locale),
        },
    }),
};
/** Resolved payloads, kept per theme: entering a workspace twice must not refetch the chunk. */
const payloadCache = new Map();
/**
 * The same payloads once SETTLED, for the callers that genuinely cannot await — the builder's
 * post-load theme staging runs after every route change and has to decide in the same tick, or the
 * canvas paints the live theme first and re-skins a moment later. Those callers warm the chunk on
 * the async side of the load and then peek here.
 */
const settled = new Map();
/** The payload IF its chunk has already arrived. Never fetches — see {@link loadThemePayload}. */
export function peekThemePayload(id) {
    return id ? settled.get(id) : undefined;
}
/**
 * The applyable half of a theme. Unknown id → `undefined`, exactly like the old sync lookup, so the
 * "stale or hand-edited URL" guards in the builder keep reading the same way.
 */
export function loadThemePayload(id) {
    if (!id)
        return Promise.resolve(undefined);
    const load = PAYLOAD_LOADERS[id];
    if (!load)
        return Promise.resolve(undefined);
    let pending = payloadCache.get(id);
    if (!pending) {
        pending = load().then((payload) => {
            settled.set(id, payload);
            return payload;
        });
        payloadCache.set(id, pending);
    }
    return pending;
}
/**
 * Meta + payload, joined — the drop-in for the sync `getThemeDefinition()` this replaced. A theme whose
 * meta exists but whose payload does not still resolves (to the meta alone), because that is what a
 * coming-soon teaser is, and `applyTheme` already guards on the missing `settings`/`documents`.
 */
export async function loadThemeDefinition(id) {
    const meta = getThemeListing(id);
    if (!meta)
        return undefined;
    return { ...meta, ...((await loadThemePayload(id)) ?? {}) };
}
//# sourceMappingURL=registry.js.map